import { useStore } from '@nanostores/react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { OnboardingAnswersResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { accountStore } from '@/services/auth/store';
import type { QueryConfig } from '../shared/react-query-types';

/**
 * The account's onboarding answers (`GET /v1/onboarding/answers`), shared by
 * every member and workspace. The endpoint is path-less and resolves the
 * account from the session's active-account header, so the key carries the
 * account id itself: a user can belong to several accounts, and switching
 * must refetch rather than serve the previous account's answers. An account
 * with no stored answers gets an empty answers object, not an error.
 */

const STALE_TIME = 60_000;

export const onboardingAnswersQueryKey = (accountId: number) => ['account', accountId, 'onboarding-answers'] as const;

/** @public Returned as the API sends it (`{ answers: { listen_targets } }`). */
export async function getOnboardingAnswers(): Promise<OnboardingAnswersResponse> {
  return await hydraUiApi.onboarding.getAnswers();
}

/** @public */
export const getOnboardingAnswersQueryOptions = (accountId: number) =>
  queryOptions({
    queryKey: onboardingAnswersQueryKey(accountId),
    queryFn: getOnboardingAnswers,
    staleTime: STALE_TIME,
    // Readers fail open to "not answered"; the default 5xx retry backoff would
    // hold the onboarding redirect's full-page loader for seconds first.
    retry: false,
    // Several observers share this key across the onboarding screen and its
    // step bodies. One retrying an errored query on mount flips it back to
    // pending for all of them, re-shows the page loader, unmounts the
    // observer, and loops.
    retryOnMount: false,
  });

/**
 * Fetches without suspending — callers fold `isLoading` into their own hold.
 * Disabled until the account is known; `useAuth` would throw on an empty store
 * and this is read from layout chrome too.
 */
export function useOnboardingAnswersOptional({
  queryConfig,
}: { queryConfig?: Omit<QueryConfig<typeof getOnboardingAnswersQueryOptions>, 'retryOnMount'> } = {}) {
  const accountId = useStore(accountStore)?.id;
  return useQuery({
    ...getOnboardingAnswersQueryOptions(accountId ?? 0),
    ...queryConfig,
    enabled: accountId != null && (queryConfig?.enabled ?? true),
    // Not overridable: see getOnboardingAnswersQueryOptions.
    retryOnMount: false,
  });
}
